// Route permission checker based on sidebar permissions
// This file extracts the permission logic from sidebar to ensure consistency

export const getMenuItemsForUserGroup = (userGroup) => {
  const menuItems = [];

  // Dashboard - available for all except beneficiaries
  if (userGroup !== 'beneficiary') {
    menuItems.push({
      path: '/dashboard',
      userGroups: ['super_user', 'agency_manager', 'npo_manager', 'team_manager', 'team_member', 'mediator', 'task_manager', 'event_manager']
    });
  }

  // User Management
  if (userGroup === 'super_user' || userGroup === 'agency_manager' || userGroup === 'npo_manager' || userGroup === 'team_manager' || userGroup === 'team_member' || userGroup === 'task_manager' || userGroup === 'event_manager') {
    menuItems.push({
      path: '/users-management',
      userGroups: ['super_user', 'agency_manager', 'npo_manager', 'team_manager', 'team_member', 'task_manager', 'event_manager']
    });
  }

  // Agencies - only for super_user
  if (userGroup === 'super_user') {
    menuItems.push({
      path: '/agencies',
      userGroups: ['super_user']
    });
  }

  // Location and Tasks - only for npo_manager
  if (userGroup === 'npo_manager') {
    menuItems.push(
      {
        path: '/locations',
        userGroups: ['npo_manager']
      },
      {
        path: '/tasks',
        userGroups: ['npo_manager']
      },
      {
        path: '/import-data',
        userGroups: ['npo_manager']
      }
    );
  }

  // Tasks - for task_manager and team_manager/team_member
  if (userGroup === 'task_manager' || userGroup === 'event_manager' || userGroup === 'team_manager' || userGroup === 'team_member') {
    menuItems.push({
      path: '/tasks',
      userGroups: ['task_manager', 'event_manager', 'team_manager', 'team_member']
    });
  }

  // Mediator specific routes
  if (userGroup === 'mediator') {
    menuItems.push(
      {
        path: '/slots-data',
        userGroups: ['mediator']
      },
      {
        path: '/create-slots',
        userGroups: ['mediator']
      },
      {
        path: '/mediator-pending-requests',
        userGroups: ['mediator']
      },
      {
        path: '/mediator-update-requests',
        userGroups: ['mediator']
      }
    );
  }

  // Team member / team manager specific routes
  if (userGroup === 'team_manager' || userGroup === 'team_member') {
    menuItems.push({
      path: '/update-requests',
      userGroups: ['team_manager', 'team_member']
    });
  }

  // NPOs - for super_user and agency_manager
  if (userGroup === 'super_user' || userGroup === 'agency_manager') {
    menuItems.push({
      path: '/npos',
      userGroups: ['super_user', 'agency_manager']
    });

    // Invoices - only for agency_manager
    if (userGroup === 'agency_manager') {
      menuItems.push({
        path: '/invoices',
        userGroups: ['agency_manager']
      });
    }
  }

  // Events/Teams - available for all user groups
  const eventsPath = userGroup === 'beneficiary' ? '/beneficiary-events' : '/tasks';
  menuItems.push({
    path: eventsPath,
    userGroups: ['super_user', 'agency_manager', 'npo_manager', 'team_manager', 'team_member', 'mediator', 'beneficiary', 'task_manager', 'event_manager']
  });

  // Reports - for super_user, agency_manager, npo_manager, mediator, team_manager, team_member, task_manager, event_manager
  if (['super_user', 'agency_manager', 'npo_manager', 'mediator', 'team_manager', 'team_member', 'task_manager', 'event_manager'].includes(userGroup)) {
    menuItems.push({
      path: '/reports',
      userGroups: ['super_user', 'agency_manager', 'npo_manager', 'mediator', 'team_manager', 'team_member', 'task_manager', 'event_manager']
    });
  }

  return menuItems;
};

// Check if a user has permission to access a specific route
export const hasRoutePermission = (userGroup, routePath) => {
  if (!userGroup || !routePath) {
    return false;
  }

  // Always allow access to public routes
  const publicRoutes = ['/login', '/sign-up', '/', '/logout'];
  if (publicRoutes.includes(routePath)) {
    return true;
  }

  // Get all menu items for the user group
  const menuItems = getMenuItemsForUserGroup(userGroup);
  
  // Check if the route exists in the user's allowed menu items
  const hasAccess = menuItems.some(item => 
    item.path === routePath && item.userGroups.includes(userGroup)
  );

  return hasAccess;
};

// Get the default route for a user group (fallback when they don't have access to requested route)
export const getDefaultRouteForUserGroup = (userGroup) => {
  if (!userGroup) {
    return '/login';
  }

  // Special case for beneficiaries - they go to beneficiary events
  if (userGroup === 'beneficiary') {
    return '/beneficiary-events';
  }

  // For all other users, default to dashboard
  return '/dashboard';
};

// Get a safe redirect path that the user has permission to access
export const getSafeRedirectPath = (userGroup, requestedPath) => {
  // If no requested path or user group, go to login
  if (!userGroup) {
    return '/login';
  }

  // If the user has permission for the requested path, use it
  if (requestedPath && hasRoutePermission(userGroup, requestedPath)) {
    return requestedPath;
  }

  // Otherwise, redirect to their default route
  return getDefaultRouteForUserGroup(userGroup);
};
